import {
  CREATION_FEE, MAX_DESCRIPTION_LENGTH, MAX_NAME_LENGTH, MAX_SOCIAL_LENGTH, MAX_SYMBOL_LENGTH,
  formatEth, formatTokens, parseEthInput, quoteBuy,
} from '@colosseum/shared';
import {toHex} from 'viem';
import {$, html, raw, setHtml} from '../ui/dom.js';
import {closeModal, notify, txProgress} from '../ui/feedback.js';
import {describeError, launch as sendLaunch} from '../tx.js';
import {navigate} from '../router.js';
import {wallet, onRightChain} from '../wallet.js';
import {initialReservedTokens} from '@colosseum/shared';
import {refresh} from '../store.js';

/**
 * Launch. One transaction: the creation fee plus an optional first buy, which
 * the factory makes on the creator's behalf and which is exempt from the
 * launch-window tax.
 */

export function launchView(): string {
  return html`<section class="subpage">
    <div class="eyebrow">Create a token</div>
    <h1>Launch.</h1>
    <p>A fixed supply of 1,000,000,000 on a bonding curve, with no liquidity from you. It graduates at 4 ETH into a
      pool that is locked forever.</p>

    <div class="launch-layout">
      <form id="launch-form" class="panel" novalidate>
        <div class="fields">
          <div class="field">
            <label for="coin-name">Name</label>
            <input id="coin-name" maxlength="${MAX_NAME_LENGTH}" placeholder="Gladiator" required>
          </div>
          <div class="field">
            <label for="coin-symbol">Ticker</label>
            <input id="coin-symbol" maxlength="${MAX_SYMBOL_LENGTH}" placeholder="GLAD" required>
          </div>
        </div>

        <div class="field">
          <label for="coin-description">Description</label>
          <textarea id="coin-description" rows="3" maxlength="${MAX_DESCRIPTION_LENGTH}"
            placeholder="What is it?"></textarea>
        </div>

        <div class="field">
          <label for="coin-logo">Image URL</label>
          <input id="coin-logo" maxlength="512" placeholder="https://… or ipfs://…" inputmode="url">
          <small>The contract stores the URL you give it. There is no upload here: host the image somewhere
            durable, ideally IPFS, because whatever you put here is what every client will read forever.</small>
        </div>

        <details class="socials">
          <summary>Links (optional)</summary>
          <div class="fields">
            <div class="field"><label for="s-twitter">X</label><input id="s-twitter" maxlength="${MAX_SOCIAL_LENGTH}"></div>
            <div class="field"><label for="s-telegram">Telegram</label><input id="s-telegram" maxlength="${MAX_SOCIAL_LENGTH}"></div>
          </div>
          <div class="fields">
            <div class="field"><label for="s-website">Website</label><input id="s-website" maxlength="${MAX_SOCIAL_LENGTH}"></div>
            <div class="field"><label for="s-discord">Discord</label><input id="s-discord" maxlength="${MAX_SOCIAL_LENGTH}"></div>
          </div>
          <div class="field"><label for="s-farcaster">Farcaster</label><input id="s-farcaster" maxlength="${MAX_SOCIAL_LENGTH}"></div>
        </details>

        <div class="field">
          <label for="first-buy">Your first buy · ETH (optional)</label>
          <input id="first-buy" type="text" inputmode="decimal" placeholder="0.0">
          <small>Bought inside the launch transaction, at the opening price, exempt from the launch-window tax.
            Everyone else pays 99% for the first three seconds.</small>
        </div>

        <div class="cost" id="launch-cost"></div>
        <button class="primary full" type="submit">Launch</button>
        <p class="form-note">One transaction. The 0.0005 ETH creation fee goes to the treasury; anything above it
          buys your own token on the curve.</p>
      </form>

      <aside class="panel launch-facts">
        <h3 class="panel-title">What happens</h3>
        <ol class="facts">
          <li><b>1,000,000,000 tokens</b> are minted once, to the curve. No mint function, no owner, no transfer
            restriction.</li>
          <li><b>The curve is the liquidity.</b> It opens at 0.000000001 ETH and rises with every purchase. You supply
            nothing.</li>
          <li><b>At 4 ETH raised</b> the curve closes, having sold 800,000,000. Its 4 ETH and 160,000,000 tokens become
            a full-range Uniswap v4 position held by a locker with no withdrawal function. The other 40,000,000 are
            burned.</li>
          <li><b>You earn 0.5%</b> of the ETH leg of every trade, on the curve and in the pool, forever. It accrues to
            a ledger you can claim from at any time.</li>
          <li><b>Another 0.5%</b> goes into the pool your token is then in the draw for.</li>
        </ol>
        <p class="muted small">You cannot pause it, change it, or take the liquidity. Neither can we.</p>
      </aside>
    </div>
  </section>`;
}

export function mountLaunch(): () => void {
  const form = $<HTMLFormElement>('#launch-form');
  const firstBuy = $<HTMLInputElement>('#first-buy');
  firstBuy?.addEventListener('input', paintCost);
  paintCost();
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    void submit();
  });
  return () => {};
}

function paintCost(): void {
  const wei = parseEthInput($<HTMLInputElement>('#first-buy')?.value ?? '') ?? 0n;
  const total = CREATION_FEE + wei;
  let receives = '';
  if (wei > 0n) {
    // A fresh curve: full supply, nothing raised, and the creator is exempt.
    const q = quoteBuy(
      {trackedQuote: 0n, trackedTokens: 1_000_000_000n * 10n ** 18n, reservedTokens: initialReservedTokens(), graduated: false},
      wei,
      0n,
    );
    receives = html`<div class="cost-row"><span>You receive</span><b>${formatTokens(q.tokensOut)} tokens</b></div>`;
  }
  setHtml(
    $('#launch-cost'),
    html`<div class="cost-row"><span>Creation fee</span><b>${formatEth(CREATION_FEE, {places: 4})} ETH</b></div>
      ${raw(wei > 0n ? html`<div class="cost-row"><span>First buy</span><b>${formatEth(wei, {places: 4})} ETH</b></div>` : '')}
      <div class="cost-row total"><span>Total</span><b>${formatEth(total, {places: 4})} ETH</b> <span class="muted">plus gas</span></div>
      ${raw(receives)}`,
  );
}

function value(id: string): string {
  return ($<HTMLInputElement | HTMLTextAreaElement>(`#${id}`)?.value ?? '').trim();
}

/** The contract's limits are in bytes, not characters, so an emoji costs four. */
function tooLong(s: string, max: number): boolean {
  return new TextEncoder().encode(s).length > max;
}

async function submit(): Promise<void> {
  if (!wallet.address) return notify('Connect a wallet first.', 'error');
  if (!onRightChain()) return notify('Switch your wallet to Robinhood Chain.', 'error');

  const name = value('coin-name');
  const symbol = value('coin-symbol').toUpperCase();
  const description = value('coin-description');
  const logo = value('coin-logo');
  if (!name || !symbol) return notify('A name and a ticker are required.', 'error');
  if (tooLong(name, MAX_NAME_LENGTH)) return notify(`The name is over ${MAX_NAME_LENGTH} bytes.`, 'error');
  if (tooLong(symbol, MAX_SYMBOL_LENGTH)) return notify(`The ticker is over ${MAX_SYMBOL_LENGTH} bytes.`, 'error');
  if (tooLong(description, MAX_DESCRIPTION_LENGTH)) return notify('The description is too long.', 'error');
  if (tooLong(logo, 512)) return notify('The image URL is too long.', 'error');

  const firstBuy = parseEthInput(value('first-buy')) ?? 0n;
  // A fresh random salt for every attempt. The factory derives the token's
  // address from the creator and this value, so nobody can compute the address
  // before the launch lands, and a retry after a failure gets a new one.
  const salt = toHex(crypto.getRandomValues(new Uint8Array(32)));
  const p = txProgress(`Launch ${symbol}`);
  try {
    const {token} = await sendLaunch(
      {
        name, symbol, logo, description,
        socials: {
          twitter: value('s-twitter'), telegram: value('s-telegram'), discord: value('s-discord'),
          website: value('s-website'), farcaster: value('s-farcaster'),
        },
        salt,
      },
      firstBuy,
      0n,
      (stage, detail, hash) => p.update(stage, detail, hash),
    );
    await refresh();
    closeModal();
    notify(`${symbol} is live.`, 'success');
    navigate(`#/token/${token.toLowerCase()}`);
  } catch (e) {
    p.fail(describeError(e));
  }
}
